import { Button, Input, Popover, Table, Tag, Typography, Select, Form, Switch, Tooltip, Collapse, Checkbox, InputNumber, Radio, Divider, Row, Col, Modal } from "antd"
import { PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined, WarningFilled, CaretRightOutlined, CopyOutlined, InfoCircleTwoTone, SendOutlined } from '@ant-design/icons'
import { ColumnsType } from "antd/lib/table/interface"
import { v4 as uuid } from 'uuid'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { allApeCodes, effectifFilterValues } from "../AllCriteriasOptions"
import { rgeFilterValues } from "../allQualifsRge"
import { getOptionsSelect } from "../CnColumns"
import { Agency, AgencyCriteriasGroup, AgencyFormula, AgencyImported, AgencyInDB, Rg2 } from "./agenciesInterfaces"
import CustomSelectImportantColumns from "./customSelectImportantColumns"
import { fetchKeywordsData, filterKeywords, getKeywordGroupsWithMembers, getKeywordsList, mergeDistinctKeywords } from "../keywords/keywords-service"
import { KeywordGroupWithMembers } from "../keywords/keywords-interfaces"
import KeywordGroupsToolbar from "../KeywordGroupsToolbar"
import { defaultValueStatus, Status } from "../CnStatus"
import ApplyFormulaToOtherAgenciesModal from "./applyFormulaToOtherAgenciesModal"
import { copyToClipboard, handleDownloadCSV } from "../../utils"
import {
    buildMsFilterOptions,
    MsEnseignesGroup,
    renderMsFilterOption,
    resolveMsSelectValues,
} from "../ms/msEnseignesGroups-service"

function onNameChange(value: string, agency_id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].name = value
    temp[index].modified = true
    setAgencies(temp)
}

function onNameCaFileChange(value: string, agency_id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].name_in_ca_file = value
    temp[index].modified = true
    setAgencies(temp)
}

function onSimplifiedLoadingChange(checked: boolean, agency_id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].simplifiedLoading = checked
    temp[index].modified = true
    setAgencies(temp)
}

function onImportantKeywordsChange(value: string[] | null, agency_id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].importantKeywords = value
    temp[index].modified = true
    setAgencies(temp)
}

function onImportantQualifsChange(value: string[] | null, agency_id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].importantQualifs = value
    temp[index].modified = true
    setAgencies(temp)
}

function getAgencies(stringifyAgencies: string): Agency[] {
    const agenciesImported = JSON.parse(stringifyAgencies) as AgencyImported[]
    const agencies: Agency[] = agenciesImported.map(a => {
        return {
            agency_id: a.agency_id,
            name: a.name,
            name_in_ca_file: a.name_in_ca_file,
            simplifiedLoading: a.simplifiedLoading || false,
            formula: a.formula ? JSON.parse(a.formula) : { group1: { ...jsonEmptyGroup, id: uuid() } },
            importantKeywords: a.importantKeywords || null,
            importantQualifs: a.importantQualifs || null
        }
    })

    return agencies
}

function getRg2(stringifyRg2s: string): Rg2[] {
    return JSON.parse(stringifyRg2s).map((e: any) => {
        return {
            agencies_group_id: e.agencies_group_id,
            negoce: e.negoce,
            agencies: e.agencies.split(";"),
            orga_id: e.orga_id,
            added: false,
            updated: false,
            deleted: false
        }
    })
}

const customTagRender = (props: any) => {
    const { label, value, closable, onClose, isMaxTag } = props;

    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Tag
            onMouseDown={onPreventMouseDown}
            closable={closable}
            onClose={onClose}
            style={{ marginRight: 3 }}
        >
            {isMaxTag ? label : value}
        </Tag>
    );
};

/** Keep only mins for still-selected keywords; drop empty map. */
function syncKeywordMinOccurrences(
    keywords: string[],
    current: Record<string, number> | undefined
): Record<string, number> | null {
    if (!current) return null;
    const next: Record<string, number> = {};
    for (const keyword of keywords) {
        const min = current[keyword];
        if (min !== undefined && min >= 1) {
            next[keyword] = min;
        }
    }
    return Object.keys(next).length > 0 ? next : null;
}

function KeywordMinOccurrenceTag(props: {
    value: string
    closable: boolean
    onClose: (e: React.MouseEvent<HTMLElement>) => void
    minOccurrences: number | undefined
    onMinChange: (keyword: string, min: number | null) => void
}) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<number | null>(props.minOccurrences ?? null);

    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const label = props.minOccurrences !== undefined && props.minOccurrences >= 1
        ? `${props.value} (≥ ${props.minOccurrences})`
        : props.value;

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (nextOpen) {
                    setDraft(props.minOccurrences ?? null);
                }
            }}
            trigger="click"
            title="Occurrence minimale"
            content={
                <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}
                >
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Laisser vide = présence seule
                    </Typography.Text>
                    <InputNumber
                        min={1}
                        precision={0}
                        placeholder="Ex: 3"
                        value={draft ?? undefined}
                        onKeyDown={(e) => e.stopPropagation()}
                        onChange={(value) => {
                            if (value === null || value === undefined) {
                                setDraft(null);
                                props.onMinChange(props.value, null);
                            } else {
                                const n = Math.floor(Number(value));
                                setDraft(n >= 1 ? n : null);
                                props.onMinChange(props.value, n >= 1 ? n : null);
                            }
                        }}
                        style={{ width: '100%' }}
                    />
                </div>
            }
        >
            <Tag
                onMouseDown={onPreventMouseDown}
                closable={props.closable}
                onClose={props.onClose}
                style={{ marginRight: 3, cursor: 'pointer' }}
            >
                {label}
            </Tag>
        </Popover>
    );
}

// oldRg2 is now computed inside the component from props


// Update a group's properties in an agency's formula
type GroupChangeValues = {
    name?: string,
    ape?: string[],
    ms?: string[],
    effectif?: string[],
    qualif?: string[],
    cog?: string[],
    keywords?: string[],
    keywordMinOccurrences?: Record<string, number> | null,
    excludeExpiredQualif?: boolean,
    notApe?: string[],
    notMs?: string[],
    notEffectif?: string[],
    notQualif?: string[],
    notCog?: string[],
    notKeywords?: string[],
    metierGroupNameEnable?: boolean,
    metierGroupNameForce?: boolean,
    purchasePerEmployees?: number | null,
    purchasePerEmployeesForce?: boolean,
    priority?: number,
    statusValue?: string
}

function onGroupChange(
    id: string,
    agency_id: string,
    newValues: GroupChangeValues,
    setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>,
    agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].formula = updateGroupById(temp[index].formula, id, newValues)
    temp[index].modified = true
    setAgencies(temp)
};

// Recursive function to update properties of a group ("name" or "not") by id
function updateGroupById(groupOrFormula: any, groupId: string, newValues: GroupChangeValues & { not?: boolean }): any {
    // If the current group has the id groupId, return a new group object with updated values
    if (groupOrFormula.id === groupId) {
        const hasCriteriasUpdate = newValues.ape !== undefined || newValues.ms !== undefined || newValues.effectif !== undefined
            || newValues.qualif !== undefined || newValues.cog !== undefined || newValues.keywords !== undefined
            || newValues.keywordMinOccurrences !== undefined
            || newValues.excludeExpiredQualif !== undefined
            || newValues.notApe !== undefined || newValues.notMs !== undefined || newValues.notEffectif !== undefined
            || newValues.notQualif !== undefined || newValues.notCog !== undefined || newValues.notKeywords !== undefined;
        const nextCriterias = hasCriteriasUpdate ? {
            ...groupOrFormula.criterias,
            ...(newValues.ape !== undefined && { ape: newValues.ape }),
            ...(newValues.ms !== undefined && { ms: newValues.ms }),
            ...(newValues.effectif !== undefined && { effectif: newValues.effectif }),
            ...(newValues.qualif !== undefined && { qualif: newValues.qualif }),
            ...(newValues.cog !== undefined && { cog: newValues.cog }),
            ...(newValues.keywords !== undefined && { keywords: newValues.keywords }),
            ...(newValues.excludeExpiredQualif !== undefined && { excludeExpiredQualif: newValues.excludeExpiredQualif }),
            ...(newValues.notApe !== undefined && { notApe: newValues.notApe }),
            ...(newValues.notMs !== undefined && { notMs: newValues.notMs }),
            ...(newValues.notEffectif !== undefined && { notEffectif: newValues.notEffectif }),
            ...(newValues.notQualif !== undefined && { notQualif: newValues.notQualif }),
            ...(newValues.notCog !== undefined && { notCog: newValues.notCog }),
            ...(newValues.notKeywords !== undefined && { notKeywords: newValues.notKeywords }),
        } : undefined;
        if (nextCriterias && newValues.keywordMinOccurrences !== undefined) {
            if (newValues.keywordMinOccurrences === null || Object.keys(newValues.keywordMinOccurrences).length === 0) {
                delete nextCriterias.keywordMinOccurrences;
            } else {
                nextCriterias.keywordMinOccurrences = newValues.keywordMinOccurrences;
            }
        }
        return {
            ...groupOrFormula, // Spread the existing properties
            ...(newValues.name !== undefined && { name: newValues.name }),
            ...(newValues.not !== undefined && { not: newValues.not }),
            ...(hasCriteriasUpdate && { criterias: nextCriterias }),
            ...(newValues.metierGroupNameEnable !== undefined && { metierGroupNameEnable: newValues.metierGroupNameEnable }),
            ...(newValues.metierGroupNameForce !== undefined && { metierGroupNameForce: newValues.metierGroupNameForce }),
            ...(newValues.purchasePerEmployees !== undefined && { purchasePerEmployees: newValues.purchasePerEmployees }),
            ...(newValues.purchasePerEmployeesForce !== undefined && { purchasePerEmployeesForce: newValues.purchasePerEmployeesForce }),
            ...(newValues.priority !== undefined && { priority: newValues.priority }),
            ...(newValues.statusValue !== undefined && { statusValue: newValues.statusValue }),
        };
    }

    // Hold the updated structure
    const updatedObj: any = Array.isArray(groupOrFormula) ? [...groupOrFormula] : { ...groupOrFormula };

    // Traverse the object recursively for nested groups (group1, group2)
    for (const key in updatedObj) {
        if (typeof updatedObj[key] === 'object' && updatedObj[key] !== null) {
            // Update nested group recursively
            updatedObj[key] = updateGroupById(updatedObj[key], groupId, newValues);
        }
    }

    return updatedObj;
};

// Update an agency's formula
function onFormulaChange(agency_id: string, newFormula: AgencyFormula, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].formula = newFormula
    temp[index].modified = true
    setAgencies(temp)
};

// Update the operator between 2 groups in an agency's formula
function onOperatorChange(agency_id: string, newOperator: string, group2Id: string, setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>, agencies: Agency[]) {
    let temp: Agency[] = Array.from(agencies)
    const index = temp.findIndex(e => e.agency_id == agency_id)
    temp[index].formula = updateOperatorByGroup2Id(temp[index].formula, group2Id, newOperator)
    temp[index].modified = true
    setAgencies(temp)
};

// Recursive function to update the operator between 2 groups knwoing the group2's id
function updateOperatorByGroup2Id(groupOrFormula: any, group2Id: string, newOperator: string): any {
    // Check if this is the correct group2
    if (groupOrFormula.operator && groupOrFormula.group2.id === group2Id) {
        return {
            ...groupOrFormula,
            operator: newOperator
        };
    }

    // Traverse the group1 if it exists
    if (groupOrFormula.group1) {
        groupOrFormula.group1 = updateOperatorByGroup2Id(groupOrFormula.group1, group2Id, newOperator);
    }

    // Traverse the group2 if it exists
    if (groupOrFormula.group2 && typeof groupOrFormula.group2 === 'object') {
        groupOrFormula.group2 = updateOperatorByGroup2Id(groupOrFormula.group2, group2Id, newOperator);
    }

    return groupOrFormula;
};

function checkGroup(group: AgencyCriteriasGroup | undefined): boolean {
    if (!group) {
        return false;
    }
    // Check if the current group has `not: true` and is not linked by `AND` or if the first group (group1) has `not: true` and the second group (group2) has `not: true`
    const notUsable = (((group.group1 && group.group1.not) || (group.group2 && group.group2.not)) && group.operator !== "AND") || (group.group1?.not && group.group2?.not);

    // Recursively check the nested groups
    const group1NotUsable = checkGroup(group.group1);
    const group2NotUsable = checkGroup(group.group2);

    // Return true if this group or any of its subgroups is not usable
    return notUsable || group1NotUsable || group2NotUsable;
};
// Function to check the overall JSON structure
function isFormulaNotUsable(jsonAgencyFormula: AgencyFormula): boolean {
    const { group1, group2, operator } = jsonAgencyFormula;

    const notUsable = ((group1.not || (group2 && group2.not)) && operator !== "AND") || group1.not;

    // Recursively check both group1 and group2
    return notUsable || checkGroup(group1) || checkGroup(group2);
};

const jsonEmptyGroup: AgencyCriteriasGroup = {
    id: "",
    type: "criterias",
    name: "Groupement",
    not: false,
    criterias: {
        ape: [],
        ms: [],
        effectif: [],
        qualif: [],
        cog: [],
        keywords: []
    }
};

function emptyCogOfFormula(formula: AgencyFormula) {
    function emptyCogInGroup(group: AgencyCriteriasGroup): AgencyCriteriasGroup {
        return {
            ...group,
            id: uuid(),
            ...(group.criterias && {
                criterias: {
                    ...group.criterias,
                    cog: []
                }
            }),
            ...(group.group1 && { group1: emptyCogInGroup(group.group1) }),
            ...(group.group2 && { group2: emptyCogInGroup(group.group2) })
        };
    }

    return {
        ...formula,
        group1: emptyCogInGroup(formula.group1),
        ...(formula.group2 && { group2: emptyCogInGroup(formula.group2) })
    };
}

function formulaContainsEmptyCogOrOnlyCog(formula: AgencyFormula): boolean {
    function checkIfCriteriasAreCorrect(criterias: any): boolean {
        // At least one non-empty property (other than cog) or cog is non-empty
        const keys = Object.keys(criterias);
        const cogNonEmpty = Array.isArray(criterias.cog) && criterias.cog.filter((item: any) => item !== "").length > 0;
        // At least one other property (not cog) is non-empty
        const otherNonEmpty = keys.some(key => key !== "cog" && key !== "excludeExpiredQualif" && Array.isArray(criterias[key]) && criterias[key].length > 0);
        // If cog is non-empty, it's correct. If cog is empty, at least one other property must be non-empty for the group to be correct
        return cogNonEmpty && otherNonEmpty;
    }

    function checkGroupForEmptyCogOrOnlyCog(group: AgencyCriteriasGroup): boolean {
        if (!group) return false;
        if (group.criterias) {
            if (!checkIfCriteriasAreCorrect(group.criterias)) {
                // This group is not correct
                return true;
            }
        }
        // Recursively check nested groups
        if (group.group1 && checkGroupForEmptyCogOrOnlyCog(group.group1)) {
            return true;
        }
        if (group.group2 && checkGroupForEmptyCogOrOnlyCog(group.group2)) {
            return true;
        }
        return false;
    }

    // Check group1 (required)
    if (checkGroupForEmptyCogOrOnlyCog(formula.group1)) {
        return true;
    }

    // Check group2 if it exists
    if (formula.group2 && checkGroupForEmptyCogOrOnlyCog(formula.group2)) {
        return true;
    }

    return false;
}

const importantKeywordsColumnName = "Mots clés affichés";
const importantQualifsColumnName = "Qualifications cibles";
const orgaImportantDataColor = '#fde6a8ff';
const agencyImportantDataColor = '#a7e4b5ff';

interface PropsCnAgencies {
    orgaId: string
    softaccessId: string
    initialAgencies: string
    initialRg2: string
    initialStatus?: string
    msEnseignes: string[]
    msGroups: MsEnseignesGroup[]
}

export interface CnAgenciesHandle {
    getSaveData: () => {
        agencies: string
        rg2: string
        deletedGroupementsIds: string
    }
    hasFormulaErrors: () => boolean
}

const CnAgencies = forwardRef<CnAgenciesHandle, PropsCnAgencies>(function CnAgencies(props, ref) {

    const [agencies, setAgencies] = React.useState<Agency[]>(getAgencies(props.initialAgencies))
    const [rg2, setRg2] = React.useState<Rg2[]>(getRg2(props.initialRg2))
    const [popoverNewOpened, setPopoverNewOpened] = React.useState<boolean>(false)
    const [PopoverNewRg2, setPopoverNewRg2] = useState<boolean>(false)
    const [groupPopOverVisible, setGroupPopOverVisible] = useState<{ [key: string]: boolean }>({});
    const [columnsPopoverVisible, setColumnsPopoverVisible] = useState<{ [key: string]: boolean }>({});
    const [applyFormulaToOtherAgenciesModalOpened, setApplyFormulaToOtherAgenciesModalOpened] = useState<{ [key: string]: boolean }>({});
    const [affectAgencyConfigurationPopoverVisible, setAffectAgencyConfigurationPopoverVisible] = useState<{ [key: string]: boolean }>({});
    const [selectedAgencies, setSelectedAgencies] = useState<string[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [keywordsSearchValue, setKeywordsSearchValue] = useState<string>('');
    const [qualifsSearchValue, setQualifsSearchValue] = useState<string>('');
    const [deletedGroupementsIds, setDeletedGroupementsIds] = useState<string[]>([]);
    const [keywordGroups, setKeywordGroups] = useState<KeywordGroupWithMembers[]>([]);
    const [keywordsLoaded, setKeywordsLoaded] = useState<boolean>(false);
    const oldRg2 = React.useRef(getRg2(props.initialRg2).map(e => ({ ...e, agencies: [...e.agencies] })));

    const importantDataColumns = [importantKeywordsColumnName, importantQualifsColumnName];

    useEffect(() => {
        fetchKeywordsData()
            .then(() => {
                setKeywordGroups(getKeywordGroupsWithMembers());
                setKeywordsLoaded(true);
            })
            .catch(() => {
                setKeywordsLoaded(true);
            });
    }, []);

    const agenciesDB: AgencyInDB[] = agencies.map(a => {
        return {
            agency_id: a.agency_id,
            name: a.name,
            name_in_ca_file: a.name_in_ca_file,
            simplifiedLoading: a.simplifiedLoading,
            modified: a.modified,
            added: a.added,
            deleted: a.deleted,
            formula: JSON.stringify(a.formula),
            importantKeywords: a.importantKeywords,
            importantQualifs: a.importantQualifs
        }
    })

    useImperativeHandle(ref, () => ({
        getSaveData: () => ({
            agencies: JSON.stringify(agenciesDB),
            rg2: JSON.stringify(rg2),
            deletedGroupementsIds: JSON.stringify(deletedGroupementsIds),
        }),
        hasFormulaErrors: () =>
            agencies.some(
                (agency) =>
                    !agency.deleted &&
                    (isFormulaNotUsable(agency.formula) || formulaContainsEmptyCogOrOnlyCog(agency.formula)),
            ),
    }), [agenciesDB, rg2, deletedGroupementsIds, agencies])

    const toggleGroupPopOver = (key: string) => {
        setGroupPopOverVisible(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleColumnsPopOver = (key: string) => {
        setColumnsPopoverVisible(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleAffectAgencyConfigurationPopOver = (key: string) => {
        // Reset selected agencies and columns when closing
        setSelectedAgencies([]);
        setSelectedColumns([]);
        setAffectAgencyConfigurationPopoverVisible(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleApplyFormulaToOtherAgenciesModal = (key: string) => {
        setApplyFormulaToOtherAgenciesModalOpened(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Update the columnsPopoverContent function
    const columnsPopoverContent = (agency: Agency) => (
        <div style={{ padding: '10px' }}>
            <div style={{ marginBottom: '20px' }}>
                <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
                    Agence: {agency?.name}
                </Typography.Text>
            </div>

            <Collapse
                items={[
                    {
                        key: '1',
                        label: "💡 P'tit tips",
                        children: (
                            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                                <li>Sélectionnez les données <strong>importantes/cibles</strong> que vous souhaitez mettre en avant pour cette agence</li>
                                <li>Si vous ne sélectionnez rien, la <strong>configuration globale de l'organisation</strong> sera utilisée</li>
                                <li>Vous pouvez personnaliser chaque colonne indépendamment</li>
                                <li>Dans les champs à sélection multiple, vous pouvez filtrer les données et sélectionner en masse uniquement les données filtrées</li>
                            </ul>
                        ),
                    }
                ]}
                style={{ marginBottom: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px' }}
            />

            {/* Status indicator for each column */}
            <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', gap: '20px', fontSize: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: agencyImportantDataColor, borderRadius: '50%' }}></div>
                        <span>Configuration personnalisée</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <div style={{ width: '12px', height: '12px', backgroundColor: orgaImportantDataColor, borderRadius: '50%' }}></div>
                        <span>Configuration de l'organisation</span>
                    </div>
                </div>
            </div>

            {/* Table that contains the configuration's values */}
            <Table
                dataSource={[{
                    importantQualifs: agency.importantQualifs,
                    importantKeywords: agency.importantKeywords
                }]}
                columns={importantColumnsData(agency)}
                pagination={false}
                showHeader={true}
                bordered={true}
                style={{ marginBottom: '25px', overflowX: 'auto' }}
            />

            <Popover
                content={(
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '600px' }}>
                        <Typography.Text>Cette action affectera la configuration actuelle de cette agence aux agences sélectionnées</Typography.Text>
                        <Typography.Text>Choisissez les données à affecter ainsi que les agences cibles</Typography.Text>
                        {/* Checkboxes to select columns that will be kept for the affectation */}
                        <Checkbox.Group
                            style={{ display: 'flex', flexDirection: 'column', maxHeight: '200px', overflowY: 'auto' }}
                            value={selectedColumns}
                            onChange={setSelectedColumns}
                            options={importantDataColumns.map(col => ({
                                label: col,
                                value: col,
                                disabled: (col === importantKeywordsColumnName && agency.importantKeywords === null) || (col === importantQualifsColumnName && agency.importantQualifs === null)
                            }))}
                        />
                        <Select
                            placeholder='Sélectionner des agences'
                            mode='multiple'
                            maxTagCount={5}
                            style={{
                                width: '100%'
                            }}
                            value={selectedAgencies}
                            options={agencies.filter(a => a.agency_id !== agency.agency_id).map(a => ({ label: a.name, value: a.agency_id }))}
                            onChange={setSelectedAgencies}
                        />
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={() => {
                                // Keywords 
                                if (selectedColumns.includes('Mots clés affichés')) {
                                    selectedAgencies.forEach(agencyId => {
                                        onImportantKeywordsChange(agency.importantKeywords, agencyId, setAgencies, agencies)
                                    })
                                }
                                // Qualifications
                                if (selectedColumns.includes('Qualifications cibles')) {
                                    selectedAgencies.forEach(agencyId => {
                                        onImportantQualifsChange(agency.importantQualifs, agencyId, setAgencies, agencies)
                                    })
                                }
                                // Close popover
                                setSelectedColumns([])
                                setSelectedAgencies([])
                                toggleAffectAgencyConfigurationPopOver(agency.agency_id)
                            }}
                        >
                            Valider
                        </Button>
                    </div>
                )}
                trigger="click"
                open={affectAgencyConfigurationPopoverVisible[agency.agency_id]}
                onOpenChange={() => toggleAffectAgencyConfigurationPopOver(agency.agency_id)}
                placement="bottom"
            >
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={agency.importantKeywords === null && agency.importantQualifs === null}
                >
                    Affecter la configuration de cette agence à d'autres agences
                </Button>
            </Popover>
        </div>
    );

    const importantColumnsData = (agency: Agency): ColumnsType<{ importantQualifs: string[] | null, importantKeywords: string[] | null }> => [
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: agency.importantKeywords === null ? orgaImportantDataColor : agencyImportantDataColor,
                            borderRadius: '50%'
                        }}
                    ></div>
                    <div style={{ fontWeight: 'bold' }}>Mots clés affichés</div>
                    <Tooltip title="Seuls les mots clés sélectionnés seront affichés (fiche entreprise, vue tableau, ...) dans les résultats pour cette agence" placement="right">
                        <InfoCircleTwoTone style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'importantKeywords',
            key: 'importantKeywords',
            width: 400,
            render: (value, record) => {
                const allOptions = getOptionsSelect('keywords', '');
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <CustomSelectImportantColumns
                            value={value}
                            allOptions={allOptions.map(option => ({ label: option.label as string, value: option.value as string }))}
                            searchValue={keywordsSearchValue}
                            setSearchValue={setKeywordsSearchValue}
                            placeholder='Sélectionner des mots clés'
                            keywordGroups={keywordGroups}
                            onChange={(selectedValues) => {
                                // update importantKeywords
                                onImportantKeywordsChange(selectedValues, agency.agency_id, setAgencies, agencies)
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="gold"
                            style={{ marginTop: '16px' }}
                            disabled={agency.importantKeywords === null}
                            onClick={() => {
                                onImportantKeywordsChange(null, agency.agency_id, setAgencies, agencies)
                            }}
                        >
                            Utiliser la configuration de l'organisation
                        </Button>
                    </div>
                );
            },
        },
        {
            title: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: agency.importantQualifs === null ? orgaImportantDataColor : agencyImportantDataColor,
                            borderRadius: '50%'
                        }}
                    ></div>
                    <div style={{ fontWeight: 'bold' }}>Qualifications cibles</div>
                    <Tooltip title="Les qualifications sélectionnées seront marquées comme 'cibles' dans les résultats pour cette agence" placement="right">
                        <InfoCircleTwoTone style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    </Tooltip>
                </div>
            ),
            dataIndex: 'importantQualifs',
            key: 'importantQualifs',
            width: 400,
            render: (value, record) => {
                const allOptions = getOptionsSelect('rge', '');
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <CustomSelectImportantColumns
                            value={value}
                            allOptions={allOptions.map(option => ({ label: option.label as string, value: option.value as string }))}
                            searchValue={qualifsSearchValue}
                            setSearchValue={setQualifsSearchValue}
                            placeholder='Sélectionner des qualifications'
                            onChange={(selectedValues) => {
                                // update importantQualifs
                                onImportantQualifsChange(selectedValues, agency.agency_id, setAgencies, agencies)
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="gold"
                            style={{ marginTop: '16px' }}
                            disabled={agency.importantQualifs === null}
                            onClick={() => {
                                onImportantQualifsChange(null, agency.agency_id, setAgencies, agencies)
                            }}
                        >
                            Utiliser la configuration de l'organisation
                        </Button>
                    </div>
                );
            },
        }
    ];


    // Button component that can add a new group in a formula
    const AddGroupButton = (jsonAgencyFormula: AgencyFormula, agency_id: string, parentGroup2Id: string, key: string) => (
        <Button
            key={key}
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            onClick={() => { addNewGroup(jsonAgencyFormula, agency_id, parentGroup2Id) }}
        />
    );


    // Component for a Modal linked to a group's button
    const getGroupPopOver = (key: string, group: AgencyCriteriasGroup, agency_id: string, numberOfGroupsInFormula: number, jsonAgencyFormula: AgencyFormula) => (
        <React.Fragment key={key}>
            <Button
                type="primary"
                icon={<EditOutlined />}
                shape='round'
                onClick={() => toggleGroupPopOver(key)}
            >
                {group.name}
            </Button>
            <Modal
                title=''
                open={groupPopOverVisible[key] || false}
                onCancel={() => toggleGroupPopOver(key)}
                footer={null}
                width={1200}
                style={{ top: 50 }}
            >
                <GroupPopOverContent
                    setAgencies={setAgencies}
                    agencies={agencies}
                    deleteGroup={deleteGroup}
                    group={group}
                    groupId={group.id}
                    agency_id={agency_id}
                    toggleGroupPopOver={toggleGroupPopOver}
                    numberOfGroupsInFormula={numberOfGroupsInFormula}
                    jsonAgencyFormula={jsonAgencyFormula}
                    status={props.initialStatus ? JSON.parse(props.initialStatus).map((e: any) => ({ ...e, tendance: e.tendance ? e.tendance : (e.defaultClient ? "Client" : "Prospect") })) : defaultValueStatus}
                    keywordGroups={keywordGroups}
                    keywordsLoaded={keywordsLoaded}
                    msEnseignes={props.msEnseignes}
                    msGroups={props.msGroups}
                />
            </Modal>
        </React.Fragment>
    );

    // Function that translates an angency's formula into components
    function renderGroups(jsonAgencyFormula: AgencyFormula, agency_id: string) {
        const numberOfGroupsInFormula = countGroups(jsonAgencyFormula);
        // Recursive function to go through groups
        function renderGroup(group: any): React.ReactNode[] {
            const formulaComponents: React.ReactNode[] = [];

            // Open parenthesis if there's nesting
            if (group.group1 && group.group2) {
                formulaComponents.push(
                    <Typography.Text key={`${agency_id}-open-${group.group2.id}`} style={{ fontSize: '22px' }}>(</Typography.Text>
                );
            }

            // Push the first group (group1 or others if there's nesting)
            if (group.group1) {
                if (group.group1.type === "criterias") {
                    formulaComponents.push(getGroupPopOver(group.group1.id, group.group1, agency_id, numberOfGroupsInFormula, jsonAgencyFormula)); // Render the popover for the group
                    if (!group.group2) {
                        formulaComponents.push(AddGroupButton(jsonAgencyFormula, agency_id, "", `${agency_id}-add-root`)); // Add button if the formula only contains 1 group1
                    }
                } else {
                    formulaComponents.push(...renderGroup(group.group1)); // Recursively push components if there's nesting
                }
            }

            // Push the operator
            if (group.operator) {
                formulaComponents.push(<Typography key={`${agency_id}-operator-${group.group2?.id ?? 'solo'}`}>OU</Typography>); // Render the operator
            }

            // Push the second group (group2 or others if there's nesting)
            if (group.group2) {
                if (group.group2.type === "criterias") {
                    formulaComponents.push(getGroupPopOver(group.group2.id, group.group2, agency_id, numberOfGroupsInFormula, jsonAgencyFormula));
                } else {
                    formulaComponents.push(...renderGroup(group.group2));
                }
            }

            // Push a close parenthesis + Add the AddGroupButton
            if (group.group1 && group.group2) {
                formulaComponents.push(
                    <Typography.Text key={`${agency_id}-close-${group.group2.id}`} style={{ fontSize: '22px' }}>)</Typography.Text>
                );
                formulaComponents.push(AddGroupButton(jsonAgencyFormula, agency_id, group.group2.id, `${agency_id}-add-${group.group2.id}`));
            }

            // Return the formulaComponents for this group
            return formulaComponents;
        }

        return renderGroup(jsonAgencyFormula);
    };

    function countGroups(group: any): number {
        if (!group) return 0;
        return 1 + countGroups(group.group1) + countGroups(group.group2);
    };


    function deleteGroup(groupIdToDelete: string, jsonAgencyFormula: AgencyFormula, agency_id: string) {
        function removeGroupFromFormula(group: any): any {
            if (group.group1 && group.group1.id === groupIdToDelete) {
                return group.group2;
            }
            if (group.group2 && group.group2.id === groupIdToDelete) {
                return group.group1;
            }

            if (group.group1) {
                const updatedGroup1 = removeGroupFromFormula(group.group1);
                if (updatedGroup1 !== group.group1) {
                    group.group1 = updatedGroup1;
                }
            }

            if (group.group2) {
                const updatedGroup2 = removeGroupFromFormula(group.group2);
                if (updatedGroup2 !== group.group2) {
                    group.group2 = updatedGroup2;
                }
            }

            return group;
        }

        let updatedFormula = removeGroupFromFormula(jsonAgencyFormula);

        if (!updatedFormula.group1 && !updatedFormula.group2) {
            updatedFormula = { group1: { ...updatedFormula } };
        }

        onFormulaChange(agency_id, updatedFormula, setAgencies, agencies);
        setDeletedGroupementsIds([...deletedGroupementsIds, groupIdToDelete]);
    };


    // Function that add a new group to an agency's formula in the right place
    function addNewGroup(jsonAgencyFormula: AgencyFormula, agency_id: string, parentGroup2Id: string) {
        // Create a new empty group object
        const newGroup: AgencyCriteriasGroup = {
            ...jsonEmptyGroup,
            id: uuid()
        }


        // Recursive function to insert the new group in the formula
        function insertGroupInFormula(group: any) {
            // Check if the current group is the parent group where we want to add the new group
            if (group.group2 && group.group2.type === "criterias" && group.group2.id === parentGroup2Id) {
                // Insert new group here, replace previous group by newGroupInFormula
                const newGroupInFormula = {
                    group1: group,
                    operator: "OR",
                    group2: newGroup
                };
                return newGroupInFormula;
            }


            if (group.group1) {
                const updatedGroup1 = insertGroupInFormula(group.group1);
                if (updatedGroup1) {
                    group.group1 = updatedGroup1;
                }
            }


            if (group.group2) {
                const updatedGroup2 = insertGroupInFormula(group.group2);
                if (updatedGroup2) {
                    group.group2 = updatedGroup2;
                }
            }


            return group;
        }


        // Case 1 : the formula only contains a group1 => create a group2 and set its value to newgroup
        if (jsonAgencyFormula.group1 && !jsonAgencyFormula.group2) {
            const newJsonAgencyFormula: AgencyFormula = {
                group1: jsonAgencyFormula.group1,
                operator: "OR",
                group2: newGroup
            };
            // Update the agency's formula
            onFormulaChange(agency_id, newJsonAgencyFormula, setAgencies, agencies);
            return;
            // Case 2 : the formula contains more than just a group1 => go through the formula to place the new group in the right place
        } else {
            const updatedFormula = insertGroupInFormula(jsonAgencyFormula);
            onFormulaChange(agency_id, updatedFormula, setAgencies, agencies);
        }
    };

    const updateFormulaForTargetAgency = (sourceFormula: AgencyFormula, preservedCog: string[]): AgencyFormula => {
        // Function to recursively update groups
        const updateGroup = (group: AgencyCriteriasGroup): AgencyCriteriasGroup => {
            const updatedGroup: AgencyCriteriasGroup = {
                ...group,
                ...(group.criterias ? {
                    id: uuid(), // Generate new unique ID
                    criterias: {
                        ...group.criterias,
                        cog: preservedCog
                    }
                } : {})
            };

            // Recursively update nested groups
            if (group.group1) {
                updatedGroup.group1 = updateGroup(group.group1);
            }

            if (group.group2) {
                updatedGroup.group2 = updateGroup(group.group2);
            }

            return updatedGroup;
        };

        // Update the main formula
        const updatedFormula: AgencyFormula = {
            ...sourceFormula,
            group1: updateGroup(sourceFormula.group1) // First group preserves COG
        };

        // Update group2 if it exists
        if (sourceFormula.group2) {
            updatedFormula.group2 = updateGroup(sourceFormula.group2);
        }

        return updatedFormula;
    };

    // Update the propagateConfiguration function to use the fixed version
    const propagateConfiguration = (masterAgency: Agency, targetAgencyIds: string[]) => {
        if (targetAgencyIds.length === 0) {
            return;
        }

        // Function to get COG from first group only
        const extractFirstGroupCog = (group: AgencyCriteriasGroup): string[] => {
            // If this group has criterias, return its COG
            if (group.criterias) {
                return group.criterias.cog || [];
            }
            // If this group has nested groups, search in group1 first
            if (group.group1) {
                return extractFirstGroupCog(group.group1);
            }
            // Fallback to empty array
            return [];
        };

        // Apply configuration to each target agency
        targetAgencyIds.forEach(targetAgencyId => {
            const targetAgencyIndex = agencies.findIndex(a => a.agency_id === targetAgencyId);
            if (targetAgencyIndex === -1) return;

            const targetAgency = agencies[targetAgencyIndex];
            const preservedCog = extractFirstGroupCog(targetAgency.formula.group1);

            // Create new formula using the fixed function
            const newFormula = updateFormulaForTargetAgency(masterAgency.formula, preservedCog);

            // Update the target agency
            onFormulaChange(targetAgencyId, newFormula, setAgencies, agencies);
        });

        // Close the modal
        toggleApplyFormulaToOtherAgenciesModal(masterAgency.agency_id);

        // Show success message
        console.log(`Configuration propagated from ${masterAgency.name} to ${targetAgencyIds.length} agencies`);
    };

    // const criteriasColumns = (agency_id: string): ColumnsType<AgencyCriteriasGroup> => [
    //     {
    //         title: 'Codes APE',
    //         dataIndex: 'ape',
    //         key: 'ape',
    //         render: (_, group) => (
    //             <Select
    //                 autoClearSearchValue={false}
    //                 mode="multiple"
    //                 allowClear
    //                 style={{ minWidth: '200px' }}
    //                 placeholder="Filtre APE"
    //                 value={group.criterias.ape}
    //                 onChange={(values) => {
    //                     const apeCodes = values.map(value => value.split(';')[0]);
    //                     onGroupChange(group.id, agency_id, { ape: apeCodes }, setAgencies, agencies);
    //                 }}
    //                 filterOption={(input, option) => option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false}
    //                 onClick={(e) => {
    //                     e.preventDefault()
    //                     e.stopPropagation()
    //                 }}
    //                 options={allApeCodes}
    //                 tagRender={customTagRender}
    //             />
    //         ),
    //     },
    //     {
    //         title: 'MS',
    //         dataIndex: 'ms',
    //         key: 'ms',
    //         render: (_, group) => (
    //             <Select
    //                 autoClearSearchValue={false}
    //                 mode="multiple"
    //                 allowClear
    //                 style={{ minWidth: '200px' }}
    //                 placeholder="Filtre MS"
    //                 value={group.criterias.ms}
    //                 onChange={(values) => {
    //                     onGroupChange(group.id, agency_id, { ms: values }, setAgencies, agencies);
    //                 }}
    //                 onClick={(e) => {
    //                     e.preventDefault()
    //                     e.stopPropagation()
    //                 }}
    //                 options={msFilterValues}
    //             />
    //         ),
    //     },
    //     {
    //         title: 'Effectif',
    //         dataIndex: 'effectif',
    //         key: 'effectif',
    //         render: (_, group) => {
    //             const allEffectifValues = effectifFilterValues?.map(option => option.value as string) || [];
    //             const isAllSelected = group.criterias.effectif.length === allEffectifValues.length;

    //             return (
    //                 <Select
    //                     showSearch
    //                     autoClearSearchValue={false}
    //                     mode="multiple"
    //                     allowClear
    //                     style={{ minWidth: '200px' }}
    //                     placeholder="Filtre effectif"
    //                     value={group.criterias.effectif}
    //                     onChange={(values) => {
    //                         onGroupChange(group.id, agency_id, { effectif: values }, setAgencies, agencies);
    //                     }}
    //                     onClick={(e) => {
    //                         e.preventDefault()
    //                         e.stopPropagation()
    //                     }}
    //                     options={effectifFilterValues}
    //                     maxTagCount={10}
    //                     popupRender={(menu) => (
    //                         <div>
    //                             <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
    //                                 <Button
    //                                     size="small"
    //                                     type="link"
    //                                     onClick={() => {
    //                                         if (isAllSelected) {
    //                                             onGroupChange(group.id, agency_id, { effectif: [] }, setAgencies, agencies);
    //                                         } else {
    //                                             onGroupChange(group.id, agency_id, { effectif: allEffectifValues }, setAgencies, agencies);
    //                                         }
    //                                     }}
    //                                     style={{ padding: 0, width: '100%', justifyContent: 'flex-start' }}
    //                                 >
    //                                     {isAllSelected ? "Désélectionner tout" : "Tout sélectionner"}
    //                                 </Button>
    //                             </div>
    //                             {menu}
    //                         </div>
    //                     )}
    //                 />
    //             )
    //         },
    //     },
    //     {
    //         title: 'Qualif (qualibat, rge...)',
    //         dataIndex: 'qualif',
    //         key: 'qualif',
    //         render: (_, group) => {
    //             const allRgeFilterValues = rgeFilterValues?.map(option => option.value as string) || [];
    //             const isAllSelected = group.criterias.qualif.length === allRgeFilterValues.length;
    //             return (
    //                 <Select
    //                     showSearch
    //                     autoClearSearchValue={false}
    //                     mode="multiple"
    //                     allowClear
    //                     style={{ minWidth: '200px' }}
    //                     placeholder="Filtre Qualif"
    //                     value={group.criterias.qualif}
    //                     onChange={(values) => {
    //                         onGroupChange(group.id, agency_id, { qualif: values }, setAgencies, agencies);
    //                     }}
    //                     onClick={(e) => {
    //                         e.preventDefault()
    //                         e.stopPropagation()
    //                     }}
    //                     maxTagCount={10}
    //                     options={rgeFilterValues}
    //                     popupRender={(menu) => (
    //                         <div>
    //                             <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
    //                                 <Button
    //                                     size="small"
    //                                     type="link"
    //                                     onClick={() => {
    //                                         if (isAllSelected) {
    //                                             onGroupChange(group.id, agency_id, { qualif: [] }, setAgencies, agencies);
    //                                         } else {
    //                                             onGroupChange(group.id, agency_id, { qualif: allRgeFilterValues }, setAgencies, agencies);
    //                                         }
    //                                     }}
    //                                     style={{ padding: 0, width: '100%', justifyContent: 'flex-start' }}
    //                                 >
    //                                     {isAllSelected ? "Désélectionner tout" : "Tout sélectionner"}
    //                                 </Button>
    //                             </div>
    //                             {menu}
    //                         </div>
    //                     )}
    //                 />
    //             )
    //         },
    //     },
    //     {
    //         title: 'Codes Communes',
    //         dataIndex: 'commune',
    //         key: 'commune',
    //         render: (_, group) => (
    //             <Input
    //                 style={{ minWidth: '200px' }}
    //                 placeholder="Codes Communes"
    //                 value={Array.isArray(group.criterias.cog) ? group.criterias.cog.join(";") : ""}
    //                 onChange={(values) => {
    //                     const cogArray = values.target.value.split(";");
    //                     onGroupChange(group.id, agency_id, { cog: cogArray }, setAgencies, agencies);
    //                 }}
    //                 allowClear
    //             />
    //         ),
    //     },
    //     {
    //         title: 'Mots clés',
    //         dataIndex: 'keywords',
    //         key: 'keywords',
    //         render: (_, group) => {
    //             const allKeywordsFilterValues = keywordsList;
    //             const isAllSelected = group.criterias.keywords.length === allKeywordsFilterValues.length;
    //             return (
    //                 <Select
    //                     showSearch
    //                     autoClearSearchValue={false}
    //                     mode="multiple"
    //                     allowClear
    //                     style={{ minWidth: '200px' }}
    //                     placeholder="Filtre mots clés"
    //                     value={group.criterias.keywords}
    //                     onChange={(values) => {
    //                         onGroupChange(group.id, agency_id, { keywords: values }, setAgencies, agencies);
    //                     }}
    //                     onClick={(e) => {
    //                         e.preventDefault()
    //                         e.stopPropagation()
    //                     }}
    //                     maxTagCount={10}
    //                     options={keywordsList.map(keyword => ({ label: keyword, value: keyword }))}
    //                     popupRender={(menu) => (
    //                         <div>
    //                             <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
    //                                 <Button
    //                                     size="small"
    //                                     type="link"
    //                                     onClick={() => {
    //                                         if (isAllSelected) {
    //                                             onGroupChange(group.id, agency_id, { keywords: [] }, setAgencies, agencies);
    //                                         } else {
    //                                             onGroupChange(group.id, agency_id, { keywords: allKeywordsFilterValues }, setAgencies, agencies);
    //                                         }
    //                                     }}
    //                                     style={{ padding: 0, width: '100%', justifyContent: 'flex-start' }}
    //                                 >
    //                                     {isAllSelected ? "Désélectionner tout" : "Tout sélectionner"}
    //                                 </Button>
    //                             </div>
    //                             {menu}
    //                         </div>
    //                     )}
    //                 />
    //             )
    //         },
    //     }
    // ];

    const columnsRg2: ColumnsType<Rg2> = [
        {
            title: 'Nom',
            dataIndex: 'negoce',
            key: 'negoce',
            render: (text) => <Typography>{text}</Typography>,
        },
        {
            title: 'Agences',
            dataIndex: 'agencies',
            key: 'agencies',
            render: (agencies_ids, { agencies_group_id }) => (
                <Select
                    showSearch
                    mode="multiple"
                    tagRender={(tagProps) => <Tag>{tagProps.label}</Tag>}
                    style={{ width: '100%' }}
                    placeholder="Agences"
                    value={agencies_ids}
                    onChange={(values) => {
                        const index = rg2.findIndex(e => e.agencies_group_id == agencies_group_id)
                        rg2[index].agencies = [...values]
                        if (rg2[index].agencies != oldRg2.current.find(e => e.agencies_group_id == agencies_group_id)?.agencies) {
                            rg2[index].updated = true
                        }
                        setRg2(Array.from(rg2))
                    }}
                    options={agencies.filter(e => !e.deleted).map(e => { return { label: e.name, value: e.agency_id } })}
                    filterOption={(input, option) => option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false}
                    allowClear
                />
            ),
        },
        {
            title: '',
            render: (_, { agencies_group_id, added }) => (
                <Button
                    danger
                    type="primary"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        let temp = Array.from(rg2)

                        if (added) {
                            temp = temp.filter(e => e.agencies_group_id != agencies_group_id)
                        } else {
                            temp = temp.map(e => {
                                if (e.agencies_group_id == agencies_group_id) {
                                    return { ...e, deleted: true }
                                }
                                else {
                                    return e
                                }
                            })
                        }
                        setRg2(temp)
                    }}
                />
            )
        }
    ]

    const columns: ColumnsType<Agency> = [
        {
            title: 'Agence',
            dataIndex: 'name',
            key: 'name',
            render: (_, { name, agency_id }) => (
                <Input
                    style={{ minWidth: '200px' }}
                    placeholder=""
                    value={name}
                    onChange={(values) => onNameChange(values.target.value, agency_id, setAgencies, agencies)}
                />
            ),
        },
        {
            title: 'Nom dans le fichier CA',
            dataIndex: 'name_in_ca_file',
            key: 'name_in_ca_file',
            render: (_, { name_in_ca_file, agency_id }) => (
                <Input
                    style={{ minWidth: '200px' }}
                    placeholder=""
                    value={name_in_ca_file}
                    onChange={(values) => onNameCaFileChange(values.target.value, agency_id, setAgencies, agencies)}
                />
            ),
        },
        {
            title: 'Optimisation',
            dataIndex: 'simplifiedLoading',
            key: 'simplifiedLoading',
            render: (_, { simplifiedLoading, agency_id }) => (
                <Switch
                    checked={simplifiedLoading}
                    onChange={(checked) => onSimplifiedLoadingChange(checked, agency_id, setAgencies, agencies)}
                />
            )
        },
        {
            title: 'Données importantes',
            dataIndex: 'importantData',
            key: 'importantData',
            render: (_, agency, index) => {
                const agency_id = agency.agency_id;
                const hasCustomConfig = agency.importantKeywords !== null || agency.importantQualifs !== null;
                return (
                    <div key={`agency-columns-${agency_id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button
                            icon={<EditOutlined />}
                            type='primary'
                            shape="round"
                            onClick={() => toggleColumnsPopOver(agency_id)}
                        />
                        <Modal
                            key={`modal-columns-${agency_id}`}
                            title={
                                <Typography.Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                                    Configuration des données importantes
                                </Typography.Title>
                            }
                            open={columnsPopoverVisible[agency_id] || false}
                            onCancel={() => toggleColumnsPopOver(agency_id)}
                            footer={null}
                            width={1200}
                            style={{ top: 20 }}
                        >
                            {columnsPopoverContent(agency)}
                        </Modal>
                        {hasCustomConfig && (
                            <Tooltip title="Cette agence utilise une configuration personnalisée">
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: agencyImportantDataColor,
                                    borderRadius: '50%'
                                }}></div>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Formule',
            dataIndex: 'formula',
            key: 'formula',
            render: (_, { agency_id, formula }, index) => (
                <div key={`formule-wrapper${index}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '5px' }}>
                    {isFormulaNotUsable(formula) &&
                        <Tooltip key={`tooltip-not-usable-${agency_id}`} title="Le premier groupement ne peut avoir la condition NON & Un groupement avec la condition NON doit être accompagné d'un autre groupement (sans condition NON) via l'opérateur ET" placement="left">
                            <WarningFilled className="error-tooltip" style={{ color: 'red', cursor: 'pointer', fontSize: '20px' }} />
                        </Tooltip>
                    }
                    {formulaContainsEmptyCogOrOnlyCog(formula) &&
                        <Tooltip key={`tooltip-empty-cog-${agency_id}`} title="La formule contient un groupement avec une liste de codes communes vide ou uniquement des critères basés sur les codes communes" placement="left">
                            <WarningFilled className="error-tooltip" style={{ color: 'red', cursor: 'pointer', fontSize: '20px' }} />
                        </Tooltip>
                    }
                    {renderGroups(formula, agency_id)}
                </div>
            ),
        },
        {
            title: '',
            dataIndex: 'duplicate',
            key: 'duplicate',
            render: (_, agency) => (
                <div style={{ display: 'flex', flexDirection: 'row', gap: '15px' }}>
                    <Tooltip title="Dupliquer l'agence">
                        <Button
                            icon={<CopyOutlined />}
                            color="primary"
                            shape="round"
                            variant="outlined"
                            onClick={() => {
                                let temp = Array.from(agencies)
                                let formulaOfAgencyWithEmtyCog = emptyCogOfFormula(agency.formula)
                                temp.push({
                                    agency_id: uuid(),
                                    name: `Agence ${temp.length + 1}`,
                                    name_in_ca_file: `Agence ${temp.length + 1}`,
                                    simplifiedLoading: true,
                                    added: true,
                                    formula: formulaOfAgencyWithEmtyCog,
                                    importantKeywords: agency.importantKeywords,
                                    importantQualifs: agency.importantQualifs
                                })
                                setAgencies(temp)
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Appliquer la même formule à une liste d'agences existantes (sans appliquer les codes communes)">
                        <Button
                            icon={<SendOutlined />}
                            color="cyan"
                            shape="round"
                            variant="outlined"
                            onClick={() => toggleApplyFormulaToOtherAgenciesModal(agency.agency_id)}
                        />
                    </Tooltip>
                    <Tooltip title="Supprimer l'agence">
                        <Button
                            icon={<DeleteOutlined />}
                            color="danger"
                            shape='round'
                            variant="filled"
                            onClick={() => {
                                if (confirm("Voulez-vous vraiment supprimer l'agence suivante: " + name + " ?")) {
                                    let temp = Array.from(agencies)

                                    if (agency.added) {
                                        temp = temp.filter(e => e.agency_id != agency.agency_id)
                                    }
                                    else {
                                        temp = temp.map(e => {
                                            if (e.agency_id == agency.agency_id) {
                                                return { ...e, deleted: true }
                                            }
                                            else {
                                                return e
                                            }
                                        })
                                    }
                                    setAgencies(temp)
                                }
                            }}
                        />
                    </Tooltip>
                    <ApplyFormulaToOtherAgenciesModal
                        key={`apply-formula-modal-${agency.agency_id}`}
                        opened={applyFormulaToOtherAgenciesModalOpened[agency.agency_id] || false}
                        onClose={() => toggleApplyFormulaToOtherAgenciesModal(agency.agency_id)}
                        selectedAgency={agency}
                        agencies={agencies}
                        propagateConfiguration={(masterAgency, targetAgencyIds) => {
                            propagateConfiguration(masterAgency, targetAgencyIds)
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <Table
                columns={columns}
                dataSource={agencies.filter(e => !e.deleted)}
                rowKey="agency_id"
                pagination={false}
                style={{ marginBottom: '10px' }}
            />
            <Popover
                content={() => {

                    const [nameNewAgency, setNameNewAgency] = React.useState<string>("")
                    const [nameNewAgencyInCaFile, setNameNewAgencyInCaFile] = React.useState<string>("")


                    useEffect(() => {
                        setTimeout(() => {
                            document.getElementById("input_name_agency")?.focus({ preventScroll: true })
                        }, 100)
                    }, [])

                    const containsSpecialCharacters = (str: string) => str.includes("'");


                    return (
                        <div>
                            <h3>Choix du nom de l'agence</h3>
                            <Input
                                id="input_name_agency"
                                type={'text'}
                                value={nameNewAgency}
                                onChange={(e) => {
                                    setNameNewAgency(e.target.value)
                                    setNameNewAgencyInCaFile(e.target.value)
                                }}
                            />
                            <br />
                            <br />
                            <p>Nom dans le fichier CA</p>
                            <Input
                                type={'text'}
                                value={nameNewAgencyInCaFile}
                                onChange={(e) => setNameNewAgencyInCaFile(e.target.value)}
                            />
                            <br />
                            <br />
                            <Button
                                icon={<SaveOutlined />}
                                disabled={nameNewAgency.length == 0 || containsSpecialCharacters(nameNewAgency) || nameNewAgencyInCaFile.length == 0}
                                type='primary'
                                shape='round'
                                onClick={() => {
                                    let temp = Array.from(agencies)
                                    temp.push({
                                        agency_id: uuid(),
                                        name: nameNewAgency,
                                        name_in_ca_file: nameNewAgencyInCaFile,
                                        simplifiedLoading: true,
                                        added: true,
                                        formula: { group1: { ...jsonEmptyGroup, id: uuid() } },
                                        importantKeywords: null,
                                        importantQualifs: null
                                    })
                                    setAgencies(temp)
                                    setNameNewAgency("")
                                    setNameNewAgencyInCaFile("")
                                    setPopoverNewOpened(false)
                                }}
                            >
                                Confirmer l'ajout
                            </Button>
                            <br />
                            <br />
                            {containsSpecialCharacters(nameNewAgency) && (
                                <p style={{ color: 'red' }}>Le nom ne doit pas contenir de '</p>
                            )}
                        </div>
                    )
                }}
                style={{ borderRadius: '20px' }}
                placement="right"
                trigger="click"
                open={popoverNewOpened}
                onOpenChange={() => setPopoverNewOpened(!popoverNewOpened)}
                destroyOnHidden
            >
                <Button icon={<PlusOutlined />} type='primary' shape='round'>Ajouter une agence</Button>
            </Popover>
            <br />
            <br />
            <Collapse
                bordered={false}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
            >
                <Collapse.Panel style={{
                    marginBottom: 24,
                    background: "#f3f3f3",
                    borderRadius: 20,
                    border: 'none',
                }} header="Gestion des rg2" key="1">
                    <Table columns={columnsRg2} dataSource={rg2.filter(e => !e.deleted)} rowKey="agencies_group_id" pagination={false} style={{ marginBottom: '10px' }} />
                    <Popover open={PopoverNewRg2} content={
                        <PopoverContent setRg2={setRg2} orgaId={props.orgaId} rg2={rg2} setPopoverNewRg2={setPopoverNewRg2} />
                    } >
                        <Button onClick={() => setPopoverNewRg2(true)} icon={<PlusOutlined />} type='primary' shape='round'>Ajouter un rg2</Button>
                    </Popover>
                </Collapse.Panel>
            </Collapse>
        </div>
    )
})

export default CnAgencies

function PopoverContent(props: { setPopoverNewRg2: React.Dispatch<React.SetStateAction<boolean>>, setRg2: React.Dispatch<React.SetStateAction<Rg2[]>>, rg2: Rg2[], orgaId: string }) {

    const [name, setName] = useState<string>("")

    return <>
        <Typography>Nom du rg2</Typography>
        <br />
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <br />
        <br />
        <Button ghost onClick={() => props.setPopoverNewRg2(false)}>Annuler</Button>
        <Button type="primary" onClick={() => {
            props.setRg2(props.rg2.concat([{ agencies_group_id: uuid(), agencies: [], orga_id: props.orgaId, negoce: name, added: true, deleted: false, updated: false }]))
            props.setPopoverNewRg2(false)
        }
        } shape="round">Ok</Button>
    </>

}

function GroupPopOverContent(props: {
    setAgencies: React.Dispatch<React.SetStateAction<Agency[]>>,
    deleteGroup: (groupId: string, jsonAgencyFormula: AgencyFormula, agency_id: string) => void,
    toggleGroupPopOver: (groupId: string) => void,
    agencies: Agency[],
    groupId: string,
    group: AgencyCriteriasGroup,
    agency_id: string,
    numberOfGroupsInFormula: number,
    jsonAgencyFormula: AgencyFormula,
    status: Status[],
    keywordGroups: KeywordGroupWithMembers[],
    keywordsLoaded: boolean,
    msEnseignes: string[],
    msGroups: MsEnseignesGroup[],
}) {

    if (props.group.criterias.keywords === undefined) {
        props.group.criterias.keywords = [];
    }

    const [excludeCriterias, setExcludeCriterias] = useState<boolean>(props.group.criterias.notApe !== undefined || props.group.criterias.notMs !== undefined || props.group.criterias.notEffectif !== undefined || props.group.criterias.notQualif !== undefined || props.group.criterias.notCog !== undefined || props.group.criterias.notKeywords !== undefined);
    const [searchValue, setSearchValue] = React.useState<string>('');

    const allEffectifValues = effectifFilterValues?.map(option => option.value as string) || [];
    const isAllEffectifSelected = props.group.criterias.effectif.length === allEffectifValues.length;
    const allApeValues = allApeCodes?.map(option => option.value as string) || [];
    const isAllApeSelected = props.group.criterias.ape.length === allApeValues.length;
    const filteredApeOptions = allApeCodes?.filter(option =>
        String(option.label).toLowerCase().includes(searchValue.toLowerCase())
    ) || [];
    const allRgeFilterValues = rgeFilterValues?.map(option => option.value as string) || [];
    const isAllRgeSelected = props.group.criterias.qualif.length === allRgeFilterValues.length;
    const allKeywordsFilterValues = getKeywordsList();
    const isAllKeywordsSelected = allKeywordsFilterValues.length > 0 && props.group.criterias.keywords.length === allKeywordsFilterValues.length;
    const filteredKeywordValues = filterKeywords(searchValue);
    const msFilterOptions = buildMsFilterOptions(props.msEnseignes, props.msGroups);
    const msExcludeFilterOptions = buildMsFilterOptions(props.msEnseignes, props.msGroups);

    const SelectOptionsToolbar = ({
        selectedValues,
        filteredValues,
        allValues,
        colName,
        onClick
    }: { selectedValues: string[], filteredValues: string[], allValues: string[], colName: string, onClick: () => void }) => {
        const isAllSelected = selectedValues.length === allValues.length;
        const selectedCount = selectedValues.length;

        return (
            <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                    {selectedCount} / {allValues.length} sélectionnés
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        type="link"
                        onClick={onClick}

                        style={{ padding: 0 }}
                    >
                        {isAllSelected ? "Désélectionner tout" :
                            filteredValues.length === allValues.length ? `Tout sélectionner (${filteredValues.length})` : `Sélectionner les résultats filtrés (${filteredValues.length})`
                        }
                    </Button>
                    {selectedCount > 10 && (
                        <>
                            <Tooltip title="Copier les éléments sélectionnés dans le presse-papiers">
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        const textToCopy = selectedValues.join("\n");
                                        copyToClipboard(textToCopy);
                                    }}
                                    style={{ padding: 0 }}
                                    icon={<i className="fa fa-copy" />}
                                >
                                    Copier
                                </Button>
                            </Tooltip>
                            <Tooltip title="Télécharger les éléments sélectionnés au format CSV">
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        const text = selectedValues.join("\n");
                                        const filename = `${colName}_selection.csv`;
                                        handleDownloadCSV(text, filename);
                                    }}
                                    style={{ padding: 0 }}
                                    icon={<i className="fa fa-download" />}
                                >
                                    Télécharger
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ padding: '10px' }}>
            {/* Input field to update the group's name & Switch button to update the group's not property */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <Form.Item label="Nom du groupement" className="no-box-shadow" style={{ flexGrow: 1, boxShadow: 'none !important', padding: '20px 20px 20px 0px' }}>
                    <Input
                        value={props.group.name}
                        onChange={(e) => {
                            onGroupChange(props.groupId, props.agency_id, { name: e.target.value }, props.setAgencies, props.agencies)
                        }}
                    />
                </Form.Item>
                <Button
                    disabled={props.numberOfGroupsInFormula <= 1}
                    onClick={() => {
                        props.deleteGroup(props.groupId, props.jsonAgencyFormula, props.agency_id)
                        props.toggleGroupPopOver(props.groupId)
                    }}
                    danger
                    type="primary"
                >
                    Supprimer
                </Button>
            </div>
            <Collapse
                items={[
                    {
                        key: '1',
                        label: "💡 P'tit tips",
                        children: (
                            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                                <p>Les options suivantes gèrent l'affichage du métier/achats accessibles dans l'outil par rapport aux différents groupements de la formule de l'agence </p>
                                <br />
                                <p>Pour le nom du métier, si la checkbox est coché, <strong>2 choix</strong> s'offrent à vous :</p>
                                <p> - par défaut, le nom du groupement s'affichera si aucun libellé APE n'a été renseigné dans le paramétrage (remplacera uniquement les valeurs "autres métiers" dans l'outil)</p>
                                <p> - si vous Choisissez "Forcer le nom du groupement dans tous les cas", le nom du groupement sera affiché dans l'information "métier" de l'outil </p>
                                <br />
                                <p>Pour les achats par employé, si la checkbox est cochée, vous pouvez définir une valeur spécifique aux entreprises du groupement</p>
                                <p> - par défaut, cette valeur sera appliquée uniquement si aucune valeur n'a été définie dans les taux d'achats accessibles</p>
                                <p> - si vous choisissez "Forcer la valeur des achats par employé dans tous les cas", la valeur sera appliquée même si un taux d'achats accessible est défini</p>
                                <br />
                                <p>Le champ priorité sert à définir quels libellés/achats accessibles seront prioritaire si un siret répond aux exigences de plusieurs groupements, 1 étant la priorité la plus haute</p>
                            </ul>
                        ),
                    }
                ]}
                style={{ marginBottom: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px' }}
            />
            <Divider />
            <Checkbox checked={props.group.metierGroupNameEnable} onChange={() => onGroupChange(props.groupId, props.agency_id, { metierGroupNameEnable: !props.group.metierGroupNameEnable }, props.setAgencies, props.agencies)}>
                Nom du groupement dans la colonne métier
            </Checkbox>
            <br />
            <br />
            {props.group.metierGroupNameEnable &&
                <>
                    <Radio.Group defaultValue={props.group.metierGroupNameForce ? "force" : "dontForce"} onChange={(e) => onGroupChange(props.groupId, props.agency_id, { metierGroupNameForce: e.target.value == "force" }, props.setAgencies, props.agencies)} buttonStyle="solid">
                        <Radio.Button value="dontForce">Nom du groupement seulement si pas de libellé APE ("Autre métier")</Radio.Button>
                        <Radio.Button value="force">Forcer le nom du groupement dans tous les cas</Radio.Button>
                    </Radio.Group>
                    <br /><br />
                    Priorité d'affichage
                    <InputNumber style={{ marginLeft: '10px' }} max={50} value={props.group.priority} onChange={(e) => onGroupChange(props.groupId, props.agency_id, { priority: e || undefined }, props.setAgencies, props.agencies)} />
                    <br /><br />
                </>
            }
            <Checkbox checked={props.group.purchasePerEmployees !== undefined && props.group.purchasePerEmployees !== null} onChange={(e) => {
                onGroupChange(props.groupId, props.agency_id, { purchasePerEmployees: (e.target.checked ? 0 : null) }, props.setAgencies, props.agencies)
            }}>
                Achats par employé spécifique au groupement
            </Checkbox>
            {
                (props.group.purchasePerEmployees !== undefined && props.group.purchasePerEmployees !== null && props.group.purchasePerEmployees >= 0) &&
                <>
                    <br /><br />
                    Valeur des achats par employé
                    <InputNumber
                        style={{ marginLeft: '10px' }}
                        min={0}
                        value={props.group.purchasePerEmployees}
                        onChange={(e) => onGroupChange(props.groupId, props.agency_id, { purchasePerEmployees: e || 0 }, props.setAgencies, props.agencies)}
                    />
                    <br /><br />
                    <Radio.Group onChange={(e) => onGroupChange(props.groupId, props.agency_id, { purchasePerEmployeesForce: e.target.value == "force" }, props.setAgencies, props.agencies)} defaultValue={props.group.purchasePerEmployeesForce ? "force" : "dontForce"} buttonStyle="solid">
                        <Radio.Button value="dontForce">Valeur appliquée si pas de valeur définie dans les taux d'achats accessibles</Radio.Button>
                        <Radio.Button value="force">Forcer la valeur des achats par employé dans tous les cas</Radio.Button>
                    </Radio.Group>
                </>
            }
            <Checkbox checked={props.group.statusValue !== undefined && props.group.statusValue !== null} onChange={(e) => {
                onGroupChange(props.groupId, props.agency_id, { statusValue: (e.target.checked ? "" : undefined) }, props.setAgencies, props.agencies)
            }}>
                Valeur de statut, initialisation spécifique
            </Checkbox>
            {
                (props.group.statusValue !== undefined && props.group.statusValue !== null) &&
                <>
                    <br /><br />
                    Valeur du statut
                    <Select
                        style={{ width: '100%' }}
                        options={props.status.map(e => {
                            return { value: e.name, label: e.name }
                        })}
                        value={props.group.statusValue}
                        onChange={(value) => onGroupChange(props.groupId, props.agency_id, { statusValue: value }, props.setAgencies, props.agencies)}
                    />
                </>
            }
            <Divider />
            {/* Table that contains the criterias' values */}
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    Filtre code APE
                    <Select
                        autoClearSearchValue={false}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filtre APE"
                        value={props.group.criterias.ape}
                        onChange={(values) => {
                            const apeCodes = values.map(value => value.split(';')[0]);
                            onGroupChange(props.group.id, props.agency_id, { ape: apeCodes }, props.setAgencies, props.agencies);
                        }}
                        filterOption={false}
                        onSearch={setSearchValue}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        options={filteredApeOptions}
                        tagRender={customTagRender}
                        maxTagCount={10}
                        onOpenChange={(open: boolean) => {
                            if (!open) {
                                setSearchValue("")
                            }
                        }}
                        popupRender={(menu) => (
                            <div>
                                <SelectOptionsToolbar
                                    selectedValues={props.group.criterias.ape}
                                    filteredValues={filteredApeOptions.map(option => option.value as string)}
                                    allValues={allApeValues}
                                    colName="APE"
                                    onClick={() => {
                                        if (isAllApeSelected) {
                                            onGroupChange(props.group.id, props.agency_id, { ape: [] }, props.setAgencies, props.agencies);
                                        } else {
                                            const codes = filteredApeOptions.map(option => option.value as string);
                                            const newValue = Array.from(new Set([...props.group.criterias.ape, ...codes]));
                                            onGroupChange(props.group.id, props.agency_id, { ape: newValue }, props.setAgencies, props.agencies);
                                        }
                                    }}
                                />
                                {menu}
                            </div>
                        )}
                    />
                </Col>
                <Col span={12}>
                    Filtre Multiscan
                    <Select
                        autoClearSearchValue={false}
                        mode="multiple"
                        allowClear
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Filtre MS"
                        value={props.group.criterias.ms}
                        onChange={(values) => {
                            onGroupChange(
                                props.group.id,
                                props.agency_id,
                                { ms: resolveMsSelectValues(values, props.msGroups) },
                                props.setAgencies,
                                props.agencies
                            );
                        }}
                        filterOption={(input, option) =>
                            (option?.title ?? "").toString().toLowerCase().includes(input.toLowerCase())
                        }
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        options={msFilterOptions}
                        optionRender={(option) =>
                            renderMsFilterOption(option.data, props.group.criterias.ms, props.msGroups)
                        }
                        menuItemSelectedIcon={null}
                    />
                </Col>
            </Row>
            <br />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    Filtre Effectif
                    <Select
                        autoClearSearchValue={false}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filtre effectif"
                        value={props.group.criterias.effectif}
                        filterOption={false}
                        onSearch={setSearchValue}
                        onChange={(values) => {
                            onGroupChange(props.group.id, props.agency_id, { effectif: values }, props.setAgencies, props.agencies);
                        }}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        options={
                            effectifFilterValues?.filter(option =>
                                String(option.label).toLowerCase().includes(searchValue.toLowerCase())
                            ) || []
                        }
                        maxTagCount={10}
                        onOpenChange={(open: boolean) => {
                            if (!open) {
                                setSearchValue("")
                            }
                        }}
                        popupRender={(menu) => {
                            const filteredEffectifValues = effectifFilterValues?.filter(option =>
                                String(option.label).toLowerCase().includes(searchValue.toLowerCase())
                            ) || [];
                            return (
                                <div>
                                    <SelectOptionsToolbar
                                        selectedValues={props.group.criterias.effectif}
                                        filteredValues={filteredEffectifValues.map(option => option.value as string) || []}
                                        allValues={allEffectifValues}
                                        colName="Effectif"
                                        onClick={() => {
                                            if (isAllEffectifSelected) {
                                                onGroupChange(props.group.id, props.agency_id, { effectif: [] }, props.setAgencies, props.agencies);
                                            } else {
                                                const actualValue = props.group.criterias.effectif;
                                                const newValue = Array.from(new Set([...actualValue, ...filteredEffectifValues.map(option => option.value as string)]));
                                                onGroupChange(props.group.id, props.agency_id, { effectif: newValue }, props.setAgencies, props.agencies);
                                            }
                                        }}
                                    />
                                    {menu}
                                </div>
                            )
                        }}
                    />
                </Col>
                <Col span={12}>
                    Filtre qualifications
                    <Select
                        autoClearSearchValue={false}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filtre Qualif"
                        value={props.group.criterias.qualif}
                        filterOption={false}
                        onSearch={setSearchValue}
                        onChange={(values) => {
                            onGroupChange(props.group.id, props.agency_id, {
                                qualif: values,
                                ...(values.length === 0 ? { excludeExpiredQualif: false } : {})
                            }, props.setAgencies, props.agencies);
                        }}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        maxTagCount={10}
                        onOpenChange={(open: boolean) => {
                            if (!open) {
                                setSearchValue("")
                            }
                        }}
                        options={
                            rgeFilterValues?.filter(option =>
                                String(option.label).toLowerCase().includes(searchValue.toLowerCase())
                            ) || []
                        }
                        popupRender={(menu) => {
                            const filteredRgeValues = rgeFilterValues?.filter(option =>
                                String(option.label).toLowerCase().includes(searchValue.toLowerCase())
                            ) || [];
                            return (
                                <div>
                                    <SelectOptionsToolbar
                                        selectedValues={props.group.criterias.qualif}
                                        filteredValues={filteredRgeValues.map(option => option.value as string) || []}
                                        allValues={allRgeFilterValues}
                                        colName="Qualifications"
                                        onClick={() => {
                                            if (isAllRgeSelected) {
                                                onGroupChange(props.group.id, props.agency_id, { qualif: [], excludeExpiredQualif: false }, props.setAgencies, props.agencies);
                                            } else {
                                                const actualValue = props.group.criterias.qualif;
                                                const newValue = Array.from(new Set([...actualValue, ...filteredRgeValues.map(option => option.value as string)]));
                                                onGroupChange(props.group.id, props.agency_id, { qualif: newValue }, props.setAgencies, props.agencies);
                                            }
                                        }}
                                    />
                                    {menu}
                                </div>
                            )
                        }}
                    />
                    {!props.group.not && props.group.criterias.qualif.length > 0 && (
                        <Checkbox
                            style={{ marginTop: '8px' }}
                            checked={props.group.criterias.excludeExpiredQualif === true}
                            onChange={(e) => {
                                onGroupChange(props.group.id, props.agency_id, { excludeExpiredQualif: e.target.checked }, props.setAgencies, props.agencies);
                            }}
                        >
                            Exclure les qualifications expirées
                        </Checkbox>
                    )}
                </Col>
            </Row>
            <br />
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    Codes Communes
                    <Input
                        style={{ width: '100%' }}
                        placeholder="Codes Communes"
                        value={Array.isArray(props.group.criterias.cog) ? props.group.criterias.cog.join(";") : ""}
                        onChange={(values) => {
                            const cogArray = values.target.value.split(";");
                            onGroupChange(props.group.id, props.agency_id, { cog: cogArray }, props.setAgencies, props.agencies);
                        }}
                        allowClear
                    />
                </Col>
                <Col span={12}>
                    Filtre mots clés
                    <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                        Cliquez sur un tag pour définir une occurrence minimale (optionnel)
                    </Typography.Text>
                    <Select
                        autoClearSearchValue={false}
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filtre mots clés"
                        value={props.group.criterias.keywords}
                        filterOption={false}
                        onSearch={setSearchValue}
                        onChange={(values) => {
                            onGroupChange(props.group.id, props.agency_id, {
                                keywords: values,
                                keywordMinOccurrences: syncKeywordMinOccurrences(values, props.group.criterias.keywordMinOccurrences)
                            }, props.setAgencies, props.agencies);
                        }}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        maxTagCount={10}
                        onOpenChange={(open: boolean) => {
                            if (!open) {
                                setSearchValue("")
                            }
                        }}
                        options={filteredKeywordValues.map(keyword => ({ label: keyword, value: keyword }))}
                        tagRender={(tagProps) => {
                            if (tagProps.isMaxTag) {
                                return (
                                    <Tag style={{ marginRight: 3 }}>
                                        {tagProps.label}
                                    </Tag>
                                );
                            }
                            return (
                                <KeywordMinOccurrenceTag
                                    value={String(tagProps.value)}
                                    closable={tagProps.closable ?? false}
                                    onClose={tagProps.onClose}
                                    minOccurrences={props.group.criterias.keywordMinOccurrences?.[String(tagProps.value)]}
                                    onMinChange={(keyword, min) => {
                                        const next = { ...(props.group.criterias.keywordMinOccurrences || {}) };
                                        if (min === null || min < 1) {
                                            delete next[keyword];
                                        } else {
                                            next[keyword] = min;
                                        }
                                        onGroupChange(props.group.id, props.agency_id, {
                                            keywordMinOccurrences: Object.keys(next).length > 0 ? next : null
                                        }, props.setAgencies, props.agencies);
                                    }}
                                />
                            );
                        }}
                        popupRender={(menu) => {
                            return (
                                <div>
                                    <SelectOptionsToolbar
                                        selectedValues={props.group.criterias.keywords}
                                        filteredValues={filteredKeywordValues}
                                        allValues={allKeywordsFilterValues}
                                        colName="Mots_clés"
                                        onClick={() => {
                                            if (isAllKeywordsSelected) {
                                                onGroupChange(props.group.id, props.agency_id, {
                                                    keywords: [],
                                                    keywordMinOccurrences: null
                                                }, props.setAgencies, props.agencies);
                                            } else {
                                                const actualValue = props.group.criterias.keywords;
                                                const newValue = mergeDistinctKeywords(actualValue, filteredKeywordValues);
                                                onGroupChange(props.group.id, props.agency_id, {
                                                    keywords: newValue,
                                                    keywordMinOccurrences: syncKeywordMinOccurrences(newValue, props.group.criterias.keywordMinOccurrences)
                                                }, props.setAgencies, props.agencies);
                                            }
                                        }}
                                    />
                                    <KeywordGroupsToolbar
                                        groups={props.keywordGroups}
                                        selectedKeywords={props.group.criterias.keywords}
                                        onSelectedKeywordsChange={(keywords) => {
                                            onGroupChange(props.group.id, props.agency_id, {
                                                keywords,
                                                keywordMinOccurrences: syncKeywordMinOccurrences(keywords, props.group.criterias.keywordMinOccurrences)
                                            }, props.setAgencies, props.agencies);
                                        }}
                                    />
                                    {menu}
                                </div>
                            )
                        }}
                    />
                </Col>
            </Row>
            <Divider />
            <Row>
                <Switch
                    checked={excludeCriterias}
                    onChange={(checked) => {
                        setExcludeCriterias(checked);
                    }}
                    style={{ marginTop: '20px' }}
                />
                <Typography style={{ marginLeft: '10px', marginTop: '20px' }}>Activer les critères d'exclusion</Typography>
            </Row>

            <Typography style={{ marginTop: '10px' }}>Si activé, les critères sélectionnés seront des critères d'exclusion pour le groupement (ex: "ne pas afficher les entreprises ayant comme code APE 12345")</Typography>
            {excludeCriterias &&
                <>
                    <Divider />
                    <Typography.Title level={5} style={{ marginTop: '10px' }}>Critères d'exclusion</Typography.Title>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            Exclure codes APE
                            <Select
                                autoClearSearchValue={false}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Exclure APE"
                                value={props.group.criterias.notApe || []}
                                onChange={(values) => {
                                    const apeCodes = values.map(value => value.split(';')[0]);
                                    onGroupChange(props.group.id, props.agency_id, { notApe: apeCodes }, props.setAgencies, props.agencies);
                                }}
                                filterOption={false}
                                onSearch={setSearchValue}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                options={filteredApeOptions}
                                tagRender={customTagRender}
                                maxTagCount={10}
                                onOpenChange={(open: boolean) => {
                                    if (!open) {
                                        setSearchValue("")
                                    }
                                }}
                                popupRender={(menu) => {
                                    const currentNotApe = props.group.criterias.notApe || [];
                                    return (
                                        <div>
                                            <SelectOptionsToolbar
                                                selectedValues={currentNotApe}
                                                filteredValues={filteredApeOptions.map(option => option.value as string)}
                                                allValues={allApeValues}
                                                colName="APE"
                                                onClick={() => {
                                                    if (currentNotApe.length === allApeValues.length) {
                                                        onGroupChange(props.group.id, props.agency_id, { notApe: [] }, props.setAgencies, props.agencies);
                                                    } else {
                                                        const codes = filteredApeOptions.map(option => option.value as string);
                                                        const newValue = Array.from(new Set([...currentNotApe, ...codes]));
                                                        onGroupChange(props.group.id, props.agency_id, { notApe: newValue }, props.setAgencies, props.agencies);
                                                    }
                                                }}
                                            />
                                            {menu}
                                        </div>
                                    );
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            Exclure Multiscan
                            <Select
                                autoClearSearchValue={false}
                                mode="multiple"
                                allowClear
                                showSearch
                                style={{ width: '100%' }}
                                placeholder="Exclure MS"
                                value={props.group.criterias.notMs || []}
                                onChange={(values) => {
                                    onGroupChange(
                                        props.group.id,
                                        props.agency_id,
                                        { notMs: resolveMsSelectValues(values, props.msGroups) },
                                        props.setAgencies,
                                        props.agencies
                                    );
                                }}
                                filterOption={(input, option) =>
                                    (option?.title ?? "").toString().toLowerCase().includes(input.toLowerCase())
                                }
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                options={msExcludeFilterOptions}
                                optionRender={(option) =>
                                    renderMsFilterOption(option.data, props.group.criterias.notMs || [], props.msGroups)
                                }
                                menuItemSelectedIcon={null}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            Exclure Effectif
                            <Select
                                autoClearSearchValue={false}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Exclure effectif"
                                value={props.group.criterias.notEffectif || []}
                                onChange={(values) => {
                                    onGroupChange(props.group.id, props.agency_id, { notEffectif: values }, props.setAgencies, props.agencies);
                                }}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                filterOption={false}
                                onSearch={setSearchValue}
                                onOpenChange={(open: boolean) => {
                                    if (!open) {
                                        setSearchValue("")
                                    }
                                }}
                                options={
                                    effectifFilterValues
                                        ?.filter(option => String(option.label).toLowerCase().includes(searchValue.toLowerCase()))
                                        .map(option => ({ label: option.label, value: option.value as string })) || []
                                }
                                maxTagCount={10}
                                popupRender={(menu) => {
                                    const filteredEffectifValues = effectifFilterValues
                                        ?.filter(option => String(option.label).toLowerCase().includes(searchValue.toLowerCase())) || [];
                                    return (
                                        <div>
                                            <SelectOptionsToolbar
                                                selectedValues={props.group.criterias.notEffectif || []}
                                                filteredValues={filteredEffectifValues.map(option => option.value as string) || []}
                                                allValues={allEffectifValues}
                                                colName="Effectif"
                                                onClick={() => {
                                                    const currentNotEffectif = props.group.criterias.notEffectif || [];
                                                    if (currentNotEffectif.length === allEffectifValues.length) {
                                                        onGroupChange(props.group.id, props.agency_id, { notEffectif: [] }, props.setAgencies, props.agencies);
                                                    } else {
                                                        const actualValue = props.group.criterias.notEffectif || [];
                                                        const newValue = Array.from(new Set([...actualValue, ...filteredEffectifValues.map(option => option.value as string)]));
                                                        onGroupChange(props.group.id, props.agency_id, { notEffectif: newValue }, props.setAgencies, props.agencies);
                                                    }
                                                }}
                                            />
                                            {menu}
                                        </div>
                                    )
                                }}
                            />
                        </Col>
                        <Col span={12}>
                            Exclure qualifications
                            <Select
                                autoClearSearchValue={false}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Exclure Qualif"
                                value={props.group.criterias.notQualif || []}
                                onChange={(values) => {
                                    onGroupChange(props.group.id, props.agency_id, { notQualif: values }, props.setAgencies, props.agencies);
                                }}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                filterOption={false}
                                onSearch={setSearchValue}
                                onOpenChange={(open: boolean) => {
                                    if (!open) {
                                        setSearchValue("")
                                    }
                                }}
                                maxTagCount={10}
                                options={
                                    rgeFilterValues
                                        ?.filter(option => String(option.label).toLowerCase().includes(searchValue.toLowerCase()))
                                        .map(option => ({ label: option.label, value: option.value as string })) || []
                                }
                                popupRender={(menu) => {
                                    const filteredRgeValues = rgeFilterValues?.filter(option => String(option.label).toLowerCase().includes(searchValue.toLowerCase())) || [];
                                    return (
                                        <div>
                                            <SelectOptionsToolbar
                                                selectedValues={props.group.criterias.notQualif || []}
                                                filteredValues={filteredRgeValues.map(option => option.value as string) || []}
                                                allValues={allRgeFilterValues}
                                                colName="Qualifications"
                                                onClick={() => {
                                                    const currentNotQualif = props.group.criterias.notQualif || [];
                                                    if (currentNotQualif.length === allRgeFilterValues.length) {
                                                        onGroupChange(props.group.id, props.agency_id, { notQualif: [] }, props.setAgencies, props.agencies);
                                                    } else {
                                                        const actualValue = props.group.criterias.notQualif || [];
                                                        const newValue = Array.from(new Set([...actualValue, ...filteredRgeValues.map(option => option.value as string)]));
                                                        onGroupChange(props.group.id, props.agency_id, { notQualif: newValue }, props.setAgencies, props.agencies);
                                                    }
                                                }}
                                            />
                                            {menu}
                                        </div>
                                    )
                                }}
                            />
                        </Col>
                    </Row>
                    <br />
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            Exclure Codes Communes
                            <Input
                                style={{ width: '100%' }}
                                placeholder="Exclure Codes Communes"
                                value={Array.isArray(props.group.criterias.notCog) ? props.group.criterias.notCog.join(";") : ""}
                                onChange={(values) => {
                                    const cogArray = values.target.value.split(";");
                                    onGroupChange(props.group.id, props.agency_id, { notCog: cogArray }, props.setAgencies, props.agencies);
                                }}
                                allowClear
                            />
                        </Col>
                        <Col span={12}>
                            Exclure mots clés
                            <Select
                                autoClearSearchValue={false}
                                mode="multiple"
                                allowClear
                                style={{ width: '100%' }}
                                placeholder="Exclure mots clés"
                                value={props.group.criterias.notKeywords || []}
                                onChange={(values) => {
                                    onGroupChange(props.group.id, props.agency_id, { notKeywords: values }, props.setAgencies, props.agencies);
                                }}
                                filterOption={false}
                                onSearch={setSearchValue}
                                onOpenChange={(open: boolean) => {
                                    if (!open) {
                                        setSearchValue("")
                                    }
                                }}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }}
                                maxTagCount={10}
                                options={filteredKeywordValues.map(keyword => ({ label: keyword, value: keyword }))}
                                popupRender={(menu) => {
                                    return (
                                        <div>
                                            <SelectOptionsToolbar
                                                selectedValues={props.group.criterias.notKeywords || []}
                                                filteredValues={filteredKeywordValues}
                                                allValues={allKeywordsFilterValues}
                                                colName="Mots_clés"
                                                onClick={() => {
                                                    const currentNotKeywords = props.group.criterias.notKeywords || [];
                                                    if (currentNotKeywords.length === allKeywordsFilterValues.length) {
                                                        onGroupChange(props.group.id, props.agency_id, { notKeywords: [] }, props.setAgencies, props.agencies);
                                                    } else {
                                                        const actualValue = props.group.criterias.notKeywords || [];
                                                        const newValue = mergeDistinctKeywords(actualValue, filteredKeywordValues);
                                                        onGroupChange(props.group.id, props.agency_id, { notKeywords: newValue }, props.setAgencies, props.agencies);
                                                    }
                                                }}
                                            />
                                            <KeywordGroupsToolbar
                                                groups={props.keywordGroups}
                                                selectedKeywords={props.group.criterias.notKeywords || []}
                                                onSelectedKeywordsChange={(keywords) => {
                                                    onGroupChange(props.group.id, props.agency_id, { notKeywords: keywords }, props.setAgencies, props.agencies);
                                                }}
                                            />
                                            {menu}
                                        </div>
                                    )
                                }}
                            />
                        </Col>
                    </Row>
                </>
            }
            {/* <Table
                    dataSource={[group]}
                    columns={criteriasColumns(agency_id)}
                    pagination={false}
                    showHeader={true}
                    bordered={true}
                    style={{
                        marginBottom: '10px',
                        overflowX: 'auto'
                    }}
                /> */}
        </div>
    )
};